/**
 * resolve-preferred-workspace.ts
 *
 * Resolves the default workspace for app initialization and refresh flows.
 *
 * Primary responsibilities:
 * - Honor explicit workspace selection when provided
 * - Prefer the current user's own home workspace by default
 * - Avoid defaulting to teammate workspaces unless explicitly selected
 *
 * Dependencies:
 * - Supabase workspace table types
 */

import type { Tables } from '@/supabase/types'

interface ResolvePreferredWorkspaceInput {
  availableWorkspaces: Tables<'workspaces'>[]
  currentUserId: string | null | undefined
  preferredWorkspaceId?: string | null
}

// --------------

const normalizeId = (value: string | null | undefined) => {
  if (typeof value !== 'string') {
    return null
  }

  const normalizedValue = value.trim()
  return normalizedValue.length > 0 ? normalizedValue : null
}

// --------------

export const resolvePreferredWorkspace = ({
  availableWorkspaces,
  currentUserId,
  preferredWorkspaceId
}: ResolvePreferredWorkspaceInput): Tables<'workspaces'> | null => {
  const normalizedPreferredWorkspaceId = normalizeId(preferredWorkspaceId)
  const preferredWorkspace = normalizedPreferredWorkspaceId
    ? availableWorkspaces.find((workspace) => workspace.id === normalizedPreferredWorkspaceId)
    : null

  if (preferredWorkspace) {
    return preferredWorkspace
  }

  const normalizedCurrentUserId = normalizeId(currentUserId)
  const ownedWorkspaces = normalizedCurrentUserId
    ? availableWorkspaces.filter((workspace) => workspace.user_id === normalizedCurrentUserId)
    : []

  return (
    ownedWorkspaces.find((workspace) => workspace.is_home) ||
    ownedWorkspaces[0] ||
    availableWorkspaces.find((workspace) => workspace.is_home) ||
    availableWorkspaces[0] ||
    null
  )
}
